import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const OrdersContainer = styled.div`
  padding: 40px 0;
`;

const OrdersHeader = styled.h1`
  font-size: 2rem;
  margin-bottom: 20px;
`;

const HeaderControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  background: transparent;
  border: 1px solid #aaa;
  color: #fff;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const PrimaryButton = styled(Button)`
  background: #2c7d46;
  border-color: #2c7d46;

  &:hover {
    background: #36964f;
  }
`;

const FilterSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
  min-width: 200px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: #aaa;
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #555;
  color: #fff;
  padding: 10px;
  border-radius: 4px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #2c7d46;
  }
`;

const Select = styled.select`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #555;
  color: #fff;
  padding: 10px;
  border-radius: 4px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #2c7d46;
  }

  option {
    background: #1a1a1a;
  }
`;

const StatsBar = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 15px 20px;
  flex: 1;
  min-width: 150px;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #aaa;
  margin-bottom: 5px;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
`;

const OrdersTable = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr 150px 120px 120px 100px 80px 100px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.1);
  font-weight: bold;
  font-size: 0.9rem;
  gap: 10px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 5px;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr 150px 120px 120px 100px 80px 100px;
  padding: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.2s ease;
  gap: 10px;
  align-items: center;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const TableCell = styled.div`
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 1024px) {
    white-space: normal;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;

  ${props => props.status === 'completed' && `
    background: rgba(44, 125, 70, 0.2);
    color: #4caf50;
  `}
  ${props => props.status === 'pending' && `
    background: rgba(255, 193, 7, 0.2);
    color: #ffc107;
  `}
  ${props => props.status === 'failed' && `
    background: rgba(170, 0, 0, 0.2);
    color: #ff6b6b;
  `}
  ${props => props.status === 'refunded' && `
    background: rgba(33, 150, 243, 0.2);
    color: #2196f3;
  `}
  ${props => props.status === 'disputed' && `
    background: rgba(156, 39, 176, 0.2);
    color: #9c27b0;
  `}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: #ccc;
  font-size: 1.2rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 10px 20px;
  color: #aaa;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1a1a1a;
  border-radius: 8px;
  padding: 30px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #aaa;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #fff;
  }
`;

const DetailSection = styled.div`
  margin-bottom: 20px;
`;

const DetailTitle = styled.h3`
  margin: 0 0 10px 0;
  font-size: 1.1rem;
  color: #2c7d46;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 10px;
  font-size: 0.9rem;
`;

const DetailLabel = styled.div`
  color: #aaa;
`;

const DetailValue = styled.div`
  color: #fff;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HistoryItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 10px;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const HistoryTime = styled.div`
  color: #aaa;
  font-size: 0.8rem;
  margin-bottom: 5px;
`;

const Notification = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 20px;
  border-radius: 4px;
  font-size: 0.9rem;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  ${props => props.type === 'success' && `
    background: rgba(44, 125, 70, 0.9);
    color: #fff;
    border: 1px solid #2c7d46;
  `}
  ${props => props.type === 'error' && `
    background: rgba(170, 0, 0, 0.9);
    color: #fff;
    border: 1px solid #a00;
  `}
`;

const RefundSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const RefundButton = styled(Button)`
  background: rgba(255, 193, 7, 0.2);
  border-color: #ffc107;
  color: #ffc107;

  &:hover {
    background: rgba(255, 193, 7, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ShippedCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #2c7d46;
`;

const DeleteButton = styled.button`
  background: rgba(170, 0, 0, 0.2);
  border: 1px solid #a00;
  color: #ff6b6b;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(170, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function AdminOrders() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [notification, setNotification] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    dateFrom: '',
    dateTo: ''
  });

  const apiUrl = process.env.REACT_APP_API_URL || `${API_URL}/api`;

  // Fetch orders and stats
  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [filters]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);

      const response = await fetch(`${apiUrl}/orders?${params.toString()}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      } else {
        throw new Error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      showNotification('error', 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${apiUrl}/orders/stats/summary`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleOrderClick = async (order) => {
    try {
      const response = await fetch(`${apiUrl}/orders/${order.orderId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedOrder(data.order);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      showNotification('error', 'Failed to load order details');
    }
  };

  const handleRefund = async () => {
    if (!selectedOrder) return;

    const confirmed = window.confirm(
      `Are you sure you want to refund order ${selectedOrder.orderId}?\n\n` +
      `Amount: $${selectedOrder.payment.total.toFixed(2)}\n` +
      `Customer: ${selectedOrder.customer.name}`
    );

    if (!confirmed) return;

    const reason = window.prompt('Enter refund reason (optional):');

    try {
      const response = await fetch(`${apiUrl}/refund`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId: selectedOrder.orderId,
          reason: reason || 'Refund requested by admin'
        })
      });

      if (response.ok) {
        showNotification('success', 'Refund processed successfully');
        setSelectedOrder(null);
        fetchOrders();
        fetchStats();
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Refund failed');
      }
    } catch (error) {
      console.error('Error processing refund:', error);
      showNotification('error', error.message || 'Failed to process refund');
    }
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2)}`;
  };

  const formatArtworkName = (artwork) => {
    // If we have itemId (e.g., "california-001"), use that
    if (artwork.itemId) {
      return artwork.itemId;
    }

    // Otherwise, capitalize collectionId and combine with title
    if (artwork.collectionId && artwork.title) {
      const collectionName = artwork.collectionId
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return `${collectionName} - ${artwork.title}`;
    }

    // Fallback to just the title
    return artwork.title || 'Unknown';
  };

  const handleShippedToggle = async (orderId, currentShippedStatus, event) => {
    event.stopPropagation(); // Prevent row click from opening modal

    const newShippedStatus = !currentShippedStatus;

    try {
      const response = await fetch(`${apiUrl}/orders/${orderId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shipped: newShippedStatus })
      });

      if (response.ok) {
        // Optimistically update local state
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.orderId === orderId
              ? { ...order, shipped: newShippedStatus }
              : order
          )
        );
        showNotification('success', `Order marked as ${newShippedStatus ? 'shipped' : 'not shipped'}`);
      } else {
        throw new Error('Failed to update shipped status');
      }
    } catch (error) {
      console.error('Error updating shipped status:', error);
      showNotification('error', 'Failed to update shipped status');
    }
  };

  const handleDeleteOrder = async (orderId, event) => {
    event.stopPropagation(); // Prevent row click from opening modal

    const confirmed = window.confirm(
      'Are you sure you want to delete this order? This action cannot be undone.'
    );

    if (!confirmed) return;

    try {
      const response = await fetch(`${apiUrl}/orders/${orderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        // Remove from local state
        setOrders(prevOrders => prevOrders.filter(order => order.orderId !== orderId));
        showNotification('success', 'Order deleted successfully');
        fetchStats(); // Refresh stats after deletion
      } else {
        throw new Error('Failed to delete order');
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      showNotification('error', 'Failed to delete order');
    }
  };

  return (
    <OrdersContainer>
      <OrdersHeader>Order Management</OrdersHeader>

      {user && (
        <div style={{ marginBottom: '15px', color: '#999', fontSize: '0.9rem' }}>
          Logged in as: <strong>{user.username}</strong>
        </div>
      )}

      <HeaderControls>
        <div></div>
        <ButtonGroup>
          <Button onClick={fetchOrders}>Refresh</Button>
          <Button onClick={() => navigate('/admin')}>Back to Admin</Button>
          <Button onClick={handleLogout}>Logout</Button>
        </ButtonGroup>
      </HeaderControls>

      {stats && (
        <StatsBar>
          <StatCard>
            <StatLabel>Total Orders</StatLabel>
            <StatValue>{stats.total}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Completed</StatLabel>
            <StatValue>{stats.completed}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Pending</StatLabel>
            <StatValue>{stats.pending}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Total Revenue</StatLabel>
            <StatValue>{formatCurrency(stats.totalRevenue)}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Avg Order Value</StatLabel>
            <StatValue>{formatCurrency(stats.averageOrderValue)}</StatValue>
          </StatCard>
        </StatsBar>
      )}

      <FilterSection>
        <FilterRow>
          <FilterGroup>
            <Label>Status</Label>
            <Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Orders</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
              <option value="disputed">Disputed</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Search</Label>
            <Input
              type="text"
              placeholder="Customer name, email, order ID..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>From Date</Label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>To Date</Label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </FilterGroup>
        </FilterRow>
      </FilterSection>

      {loading ? (
        <LoadingContainer>Loading orders...</LoadingContainer>
      ) : orders.length === 0 ? (
        <EmptyState>
          <p>No orders found</p>
          <p style={{ fontSize: '0.9rem', color: '#666' }}>
            {filters.status !== 'all' || filters.search || filters.dateFrom || filters.dateTo
              ? 'Try adjusting your filters'
              : 'Orders will appear here after customers make purchases'}
          </p>
        </EmptyState>
      ) : (
        <OrdersTable>
          <TableHeader>
            <div>Order ID</div>
            <div>Customer</div>
            <div>Artwork</div>
            <div>Amount</div>
            <div>Date</div>
            <div>Status</div>
            <div>Shipped</div>
            <div>Delete</div>
          </TableHeader>
          {orders.map(order => (
            <TableRow key={order.orderId} onClick={() => handleOrderClick(order)}>
              <TableCell>{order.orderId.substring(0, 8)}...</TableCell>
              <TableCell>
                <div>{order.customer.name}</div>
                <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{order.customer.email}</div>
              </TableCell>
              <TableCell>{formatArtworkName(order.artwork)}</TableCell>
              <TableCell>{formatCurrency(order.payment.total)}</TableCell>
              <TableCell>{formatDate(order.timestamp)}</TableCell>
              <TableCell>
                <StatusBadge status={order.status}>{order.status}</StatusBadge>
              </TableCell>
              <TableCell>
                <ShippedCheckbox
                  type="checkbox"
                  checked={order.shipped || false}
                  onChange={(e) => handleShippedToggle(order.orderId, order.shipped, e)}
                  onClick={(e) => e.stopPropagation()}
                />
              </TableCell>
              <TableCell>
                <DeleteButton onClick={(e) => handleDeleteOrder(order.orderId, e)}>
                  Delete
                </DeleteButton>
              </TableCell>
            </TableRow>
          ))}
        </OrdersTable>
      )}

      {selectedOrder && (
        <Modal onClick={() => setSelectedOrder(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Order Details</ModalTitle>
              <CloseButton onClick={() => setSelectedOrder(null)}>&times;</CloseButton>
            </ModalHeader>

            <DetailSection>
              <DetailTitle>Order Information</DetailTitle>
              <DetailGrid>
                <DetailLabel>Order ID:</DetailLabel>
                <DetailValue>{selectedOrder.orderId}</DetailValue>
                <DetailLabel>Status:</DetailLabel>
                <DetailValue>
                  <StatusBadge status={selectedOrder.status}>{selectedOrder.status}</StatusBadge>
                </DetailValue>
                <DetailLabel>Date:</DetailLabel>
                <DetailValue>{formatDate(selectedOrder.timestamp)}</DetailValue>
              </DetailGrid>
            </DetailSection>

            <DetailSection>
              <DetailTitle>Customer Information</DetailTitle>
              <DetailGrid>
                <DetailLabel>Name:</DetailLabel>
                <DetailValue>{selectedOrder.customer.name}</DetailValue>
                <DetailLabel>Email:</DetailLabel>
                <DetailValue>{selectedOrder.customer.email}</DetailValue>
                <DetailLabel>Address:</DetailLabel>
                <DetailValue>
                  {selectedOrder.customer.address}<br />
                  {selectedOrder.customer.city}, {selectedOrder.customer.state} {selectedOrder.customer.zip}
                </DetailValue>
              </DetailGrid>
            </DetailSection>

            <DetailSection>
              <DetailTitle>Artwork Information</DetailTitle>
              <DetailGrid>
                <DetailLabel>Title:</DetailLabel>
                <DetailValue>{formatArtworkName(selectedOrder.artwork)}</DetailValue>
                <DetailLabel>Medium:</DetailLabel>
                <DetailValue>{selectedOrder.artwork.medium || 'N/A'}</DetailValue>
                <DetailLabel>Size:</DetailLabel>
                <DetailValue>{selectedOrder.artwork.size || 'N/A'}</DetailValue>
                <DetailLabel>Price:</DetailLabel>
                <DetailValue>{formatCurrency(selectedOrder.artwork.price)}</DetailValue>
              </DetailGrid>
            </DetailSection>

            <DetailSection>
              <DetailTitle>Payment Information</DetailTitle>
              <DetailGrid>
                <DetailLabel>Square Payment ID:</DetailLabel>
                <DetailValue style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>
                  {selectedOrder.payment.squarePaymentId || 'N/A'}
                </DetailValue>
                <DetailLabel>Amount:</DetailLabel>
                <DetailValue>{formatCurrency(selectedOrder.payment.amount)}</DetailValue>
                <DetailLabel>Sales Tax:</DetailLabel>
                <DetailValue>{formatCurrency(selectedOrder.payment.salesTax)}</DetailValue>
                <DetailLabel>Shipping:</DetailLabel>
                <DetailValue>{formatCurrency(selectedOrder.payment.shipping)}</DetailValue>
                <DetailLabel>Total:</DetailLabel>
                <DetailValue><strong>{formatCurrency(selectedOrder.payment.total)}</strong></DetailValue>
                <DetailLabel>Payment Method:</DetailLabel>
                <DetailValue>{selectedOrder.payment.method}</DetailValue>
              </DetailGrid>
            </DetailSection>

            {selectedOrder.refunds && selectedOrder.refunds.length > 0 && (
              <DetailSection>
                <DetailTitle>Refunds</DetailTitle>
                {selectedOrder.refunds.map((refund, index) => (
                  <DetailGrid key={index} style={{ marginBottom: '10px' }}>
                    <DetailLabel>Amount:</DetailLabel>
                    <DetailValue>{formatCurrency(refund.amount)}</DetailValue>
                    <DetailLabel>Date:</DetailLabel>
                    <DetailValue>{formatDate(refund.timestamp)}</DetailValue>
                    <DetailLabel>Reason:</DetailLabel>
                    <DetailValue>{refund.reason}</DetailValue>
                  </DetailGrid>
                ))}
              </DetailSection>
            )}

            <DetailSection>
              <DetailTitle>Order History</DetailTitle>
              <HistoryList>
                {selectedOrder.history.map((event, index) => (
                  <HistoryItem key={index}>
                    <HistoryTime>{formatDate(event.timestamp)}</HistoryTime>
                    <div>
                      <strong>{event.action}</strong> - {event.note}
                    </div>
                  </HistoryItem>
                ))}
              </HistoryList>
            </DetailSection>

            {selectedOrder.status === 'completed' && (
              <RefundSection>
                <RefundButton onClick={handleRefund}>
                  Process Refund
                </RefundButton>
              </RefundSection>
            )}
          </ModalContent>
        </Modal>
      )}

      {notification && (
        <Notification type={notification.type}>
          {notification.message}
        </Notification>
      )}
    </OrdersContainer>
  );
}

export default AdminOrders;
